import { useState } from 'react';
import type { SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { PageShell } from '../components/PageShell.tsx';
import { LoadingDots } from '../components/LoadingDots.tsx';
import { useZoneCalc } from '../hooks/useZoneCalc.ts';
import { useStore } from '../lib/store.ts';
import type { Zone } from '../types/api.ts';

const ZONE_COLORS: Record<number, string> = {
  1: 'bg-gray-500',
  2: 'bg-blue-500',
  3: 'bg-green-500',
  4: 'bg-orange-500',
  5: 'bg-red-500',
};

const ZONE_DESC_KEYS: Record<number, string> = {
  1: 'zones.desc1',
  2: 'zones.desc2',
  3: 'zones.desc3',
  4: 'zones.desc4',
  5: 'zones.desc5',
};

function ZoneBar({ zone, ftp, t }: Readonly<{ zone: Zone; ftp: number; t: (key: string) => string }>) {
  const pctLow = Math.round((zone.lower / ftp) * 100);
  const pctHigh = zone.upper > 9000 ? '\u221E' : Math.round((zone.upper / ftp) * 100);
  const width = Math.min(((zone.zone / 5) * 100), 100);

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-sm">
        <span className="font-semibold text-(--color-text-primary)">
          Z{zone.zone} — {zone.name}
        </span>
        <span className="text-(--color-text-secondary)">
          {zone.lower}–{zone.upper > 9000 ? '\u221E' : zone.upper}W ({pctLow}–{pctHigh}%)
        </span>
      </div>
      <div className="w-full bg-(--color-bg-primary) rounded-full h-4 overflow-hidden">
        <div
          className={`h-full rounded-full ${ZONE_COLORS[zone.zone]} transition-all duration-700 ease-out`}
          style={{ width: `${width}%` }}
        />
      </div>
      <p className="text-xs text-(--color-text-secondary)">{t(ZONE_DESC_KEYS[zone.zone])}</p>
    </div>
  );
}

export function Zones() {
  const { t } = useTranslation();
  const storedFTP = useStore((s) => s.ftp);
  const profileFTP = useStore((s) => s.user?.profile?.ftp ?? null);
  const [ftpOverride, setFtpOverride] = useState<string | null>(null);
  const ftp = ftpOverride ?? (storedFTP ?? profileFTP)?.toString() ?? '';
  const setFtp = (v: string) => setFtpOverride(v);
  const mutation = useZoneCalc();

  const handleSubmit = (e: SyntheticEvent) => {
    e.preventDefault();
    const val = Number.parseInt(ftp, 10);
    if (val > 0) mutation.mutate(val);
  };

  return (
    <PageShell title={t('zones.title')} subtitle={t('zones.subtitle')}>
      <form onSubmit={handleSubmit} className="flex gap-3 mb-8">
        <input
          type="number"
          value={ftp}
          onChange={(e) => setFtp(e.target.value)}
          placeholder={t('zones.ftpPlaceholder')}
          min={1}
          className="flex-1 bg-(--color-bg-tertiary) text-(--color-text-primary) rounded-lg px-4 py-3 text-sm border border-(--color-border) focus:outline-none focus:border-(--color-accent)"
        />
        <button
          type="submit"
          disabled={mutation.isPending}
          className="bg-green-400 text-gray-900 px-8 py-3 rounded-xl font-bold text-sm shadow-lg shadow-(--color-accent)/30 hover:brightness-110 active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none transition-all"
        >
          {t('zones.calculate')}
        </button>
      </form>

      {mutation.isPending && <LoadingDots text={t('zones.calculating')} />}

      {mutation.data && (
        <div className="space-y-5 bg-(--color-bg-secondary) rounded-xl p-6 border border-(--color-border)">
          <div className="text-center mb-4">
            <span className="text-sm text-(--color-text-secondary)">{t('zones.yourFTP')}</span>
            <div className="text-3xl font-bold text-(--color-accent)">{mutation.data.ftp}W</div>
          </div>
          {mutation.data.zones.map((zone) => (
            <ZoneBar key={zone.zone} zone={zone} ftp={mutation.data.ftp} t={t} />
          ))}
        </div>
      )}
    </PageShell>
  );
}
